// Lightweight error reporting service.
// Captures errors in an in-memory ring buffer and logs them in development.
// Designed to be extended later with an external reporting endpoint.

const MAX_BUFFER_SIZE = 50;

export interface ErrorEntry {
  error: Error;
  context?: Record<string, string>;
  timestamp: number;
}

const errorBuffer: ErrorEntry[] = [];

/**
 * Report an error. In development mode the error and its context are logged
 * to the console. Every error is stored in a fixed-size in-memory ring
 * buffer (most recent 50) for later inspection.
 *
 * The function is a no-throw: it will never propagate an exception.
 */
export function reportError(error: Error, context?: Record<string, string>): void {
  try {
    const entry: ErrorEntry = {
      error,
      context,
      timestamp: Date.now(),
    };

    // Append and trim to the last MAX_BUFFER_SIZE entries.
    errorBuffer.push(entry);
    if (errorBuffer.length > MAX_BUFFER_SIZE) {
      errorBuffer.shift();
    }

    if (import.meta.env.DEV) {
      if (context && Object.keys(context).length > 0) {
        console.error('[ErrorReporting]', error, context);
      } else {
        console.error('[ErrorReporting]', error);
      }
    }

    // ----- Future hook -----
    // Replace the block below with a fetch() call to your external error
    // tracking service (e.g. Sentry, Datadog, a custom endpoint).
    // Example:
    //   fetch('/api/errors', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({
    //       message: error.message,
    //       stack: error.stack,
    //       context,
    //       timestamp: entry.timestamp,
    //     }),
    //   }).catch(() => { /* swallow network errors */ });
  } catch {
    // reportError itself must never throw.
  }
}

/**
 * Return a shallow copy of the in-memory error buffer (most recent last).
 * Useful for rendering in a Settings/debug panel or inspecting from
 * the browser console.
 */
export function getRecentErrors(): ReadonlyArray<ErrorEntry> {
  return [...errorBuffer];
}

/**
 * Install global handlers for uncaught errors and unhandled promise
 * rejections.  Call once at application startup (before rendering).
 */
export function setupGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
  ) => {
    reportError(
      error ?? new Error(String(message)),
      {
        source: source ?? 'unknown',
        line: String(lineno ?? ''),
        column: String(colno ?? ''),
        handler: 'window.onerror',
      },
    );
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    reportError(error, { handler: 'window.onunhandledrejection' });
  };
}
